import { createContext, useContext, useMemo } from "react";

import { SpinnerFullPage } from "src/core/components/custom-ui/spinner-full-page";
import { IRiskOption, ISelectOption } from "src/core/constants/options";
import { useActivityWithRiskOptions } from "src/core/hooks/common/useActivityWithRiskOptions";
import { useCountryWithRiskOptions } from "src/core/hooks/common/useCountryWithRiskOptions";
import { useLegalStatusOptions } from "src/core/hooks/common/useLegalStatusOptions";
import { useLegalStatusOptionsPPH } from "src/core/hooks/common/useLegalStatusOptionsPPH";
import { useLegalTypeWithRiskOptions } from "src/core/hooks/common/useLegalTypeWithRiskOptions";
import { useMissionTypeWithRiskOptions } from "src/core/hooks/common/useMissionTypeWithRiskOptions";

export type RiskOptionsContextValue = {
  countryWithRiskOptions: IRiskOption[];
  legalTypesWithRiskOptions: IRiskOption[];
  activityWithRiskOptions: IRiskOption[];
  missionTypeWithRiskOptions: IRiskOption[];
  legalStatusOptions: ISelectOption[];
  legalStatusPPHOptions: ISelectOption[];
};

export const RiskOptionsContext = createContext<RiskOptionsContextValue | undefined>(undefined);

export const useRiskOptions = (): RiskOptionsContextValue => {
  const context = useContext(RiskOptionsContext);
  if (context === undefined) {
    throw new Error("useRiskOptions must be used within a RiskOptionsProvider");
  }

  return context;
};

export const RiskOptionsProvider = ({ children }: { children: React.ReactNode }) => {
  const { countryWithRiskOptions, loading: loadingCountries } = useCountryWithRiskOptions();
  const { legalTypesWithRiskOptions, loading: loadingLegalTypes } = useLegalTypeWithRiskOptions();
  const { activityWithRiskOptions, loading: loadingActivity } = useActivityWithRiskOptions();
  const { missionTypeWithRiskOptions, loading: loadingMissions } = useMissionTypeWithRiskOptions();
  const { legalStatusOptions, loading: loadingLegalStatus } = useLegalStatusOptions();
  const { legalStatusOptions: legalStatusPPHOptions, loading: loadingLegalStatusPPH } =
    useLegalStatusOptionsPPH();

  const loading =
    loadingCountries ||
    loadingLegalTypes ||
    loadingActivity ||
    loadingMissions ||
    loadingLegalStatus ||
    loadingLegalStatusPPH;

  const value = useMemo(
    () => ({
      countryWithRiskOptions,
      legalTypesWithRiskOptions,
      activityWithRiskOptions,
      missionTypeWithRiskOptions,
      legalStatusOptions,
      legalStatusPPHOptions,
    }),
    [
      countryWithRiskOptions,
      legalTypesWithRiskOptions,
      activityWithRiskOptions,
      missionTypeWithRiskOptions,
      legalStatusOptions,
      legalStatusPPHOptions,
    ]
  );

  if (loading) {
    return <SpinnerFullPage />;
  }

  return <RiskOptionsContext.Provider value={value}>{children}</RiskOptionsContext.Provider>;
};
